import React, { useState, useCallback, useEffect, useRef } from 'react';
import { YearbookPage, PAGE_WIDTH, PAGE_HEIGHT } from '@/types/yearbook';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ReadOnlyElement from './ReadOnlyElement';

interface PreviewViewProps {
  pages: YearbookPage[];
  singlePageMode?: boolean;
}

const BLANK_PAGE: YearbookPage = {
  id: '__blank__',
  pageNumber: 0,
  elements: [],
  background: { type: 'color', value: '#ffffff' },
};

function buildSpreads(pages: YearbookPage[]): YearbookPage[][] {
  if (pages.length === 0) return [];
  const coverFront = pages.find((p) => p.coverType === 'front');
  const coverBack = pages.find((p) => p.coverType === 'back');
  const interior = pages.filter((p) => !p.coverType);
  const spreads: YearbookPage[][] = [];

  if (coverFront) spreads.push([coverFront]);

  if (interior.length > 0) {
    spreads.push([BLANK_PAGE, interior[0]]);
    for (let i = 1; i < interior.length; i += 2) {
      if (i + 1 < interior.length) {
        spreads.push([interior[i], interior[i + 1]]);
      } else {
        spreads.push([interior[i]]);
      }
    }
    const lastSpread = spreads[spreads.length - 1];
    if (lastSpread.length === 1 && lastSpread[0] !== coverFront) {
      lastSpread.push(BLANK_PAGE);
    } else {
      spreads.push([BLANK_PAGE]);
    }
  }

  if (coverBack) spreads.push([coverBack]);
  return spreads;
}

function buildSinglePages(pages: YearbookPage[]): YearbookPage[] {
  if (pages.length === 0) return [];
  const coverFront = pages.find((p) => p.coverType === 'front');
  const coverBack = pages.find((p) => p.coverType === 'back');
  const interior = pages.filter((p) => !p.coverType);
  const result: YearbookPage[] = [];
  if (coverFront) result.push(coverFront);
  result.push(...interior);
  if (coverBack) result.push(coverBack);
  return result;
}

const SPREAD_GAP = 2;
const FLIP_DURATION = 600;

const BG_BLEED = 14.1732;

const PreviewView: React.FC<PreviewViewProps> = ({ pages, singlePageMode = false }) => {
  const [currentSpread, setCurrentSpread] = useState(0);
  const [currentSinglePage, setCurrentSinglePage] = useState(0);
  const [flip, setFlip] = useState<{
    direction: 'next' | 'prev';
    fromSpread: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.6);

  const spreads = buildSpreads(pages);
  const singlePages = singlePageMode ? buildSinglePages(pages) : [];

  const computeScale = useCallback(() => {
    if (!containerRef.current) return;
    if (singlePageMode) {
      const padX = 32, padY = 80;
      const availW = containerRef.current.clientWidth - padX;
      const availH = containerRef.current.clientHeight - padY;
      const s = Math.min(availW / PAGE_WIDTH, availH / PAGE_HEIGHT, 1);
      setScale(Math.max(0.25, s));
    } else {
      const padX = 120, padY = 80;
      const availW = containerRef.current.clientWidth - padX;
      const availH = containerRef.current.clientHeight - padY;
      const spreadW = PAGE_WIDTH * 2 + SPREAD_GAP;
      const s = Math.min(availW / spreadW, availH / PAGE_HEIGHT, 1);
      setScale(Math.max(0.25, s));
    }
  }, [singlePageMode]);

  useEffect(() => {
    computeScale();
    const obs = new ResizeObserver(computeScale);
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [computeScale]);

  const goTo = useCallback(
    (idx: number) => {
      if (singlePageMode) {
        if (idx < 0 || idx >= singlePages.length) return;
        setCurrentSinglePage(idx);
        return;
      }
      if (idx < 0 || idx >= spreads.length || flip) return;
      const direction = idx > currentSpread ? 'next' : 'prev';
      setFlip({ direction, fromSpread: currentSpread });
      setTimeout(() => {
        setCurrentSpread(idx);
        setFlip(null);
      }, FLIP_DURATION);
    },
    [singlePageMode, singlePages.length, spreads.length, flip, currentSpread]
  );

  const currentIdx = singlePageMode ? currentSinglePage : currentSpread;
  const maxIdx = singlePageMode ? singlePages.length - 1 : spreads.length - 1;

  const prev = useCallback(() => goTo(currentIdx - 1), [goTo, currentIdx]);
  const next = useCallback(() => goTo(currentIdx + 1), [goTo, currentIdx]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prev, next]);

  const pageW = PAGE_WIDTH * scale;
  const pageH = PAGE_HEIGHT * scale;

  const renderPageContent = (page: YearbookPage) => {
    const bgIsImage = page.background.type !== 'color';
    const hasUserBg = bgIsImage && (page.background.trame_background_id != null || page.background.trame_background_custom_id != null);
    const bgInset = hasUserBg ? BG_BLEED : 0;
    const bgStyle: React.CSSProperties = bgIsImage
      ? {}
      : { backgroundColor: page.background.value };
    return (
      <div className="relative overflow-hidden" style={{ width: PAGE_WIDTH * scale, height: PAGE_HEIGHT * scale }}>
        {bgIsImage && (
          <img
            src={page.background.value}
            alt=""
            className="absolute pointer-events-none"
            style={{
              top: -bgInset * scale,
              left: -bgInset * scale,
              width: (PAGE_WIDTH + bgInset * 2) * scale,
              height: (PAGE_HEIGHT + bgInset * 2) * scale,
              maxWidth: 'none',
              objectFit: 'cover',
            }}
            draggable={false}
          />
        )}
        <div
          className="origin-top-left overflow-hidden"
          style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT, transform: `scale(${scale})`, ...bgStyle }}
        >
          {page.elements.map((el) => (
            <ReadOnlyElement key={el.id} element={el} />
          ))}
        </div>
      </div>
    );
  };

  const pageLabel = (page: YearbookPage) => {
    if (page.id === '__blank__') return null;
    if (page.coverType === 'front') return '1ère de couverture';
    if (page.coverType === 'back') return '4ème de couverture';
    return `Page ${page.pageNumber}`;
  };

  const pageNum = (page: YearbookPage): number | null => {
    if (page.id === '__blank__' || page.coverType) return null;
    return page.pageNumber;
  };

  const totalPages = pages.length;

  // ─── Single page mode (mobile) ───
  if (singlePageMode) {
    const page = singlePages[currentSinglePage];
    if (!page) return null;
    const label = pageLabel(page) || '';

    return (
      <div
        ref={containerRef}
        className="flex-1 w-full flex flex-col items-center justify-center bg-editor-canvas relative select-none overflow-hidden"
      >
        <div
          className="relative editor-shadow overflow-hidden"
          style={{ width: pageW, height: pageH }}
        >
          {renderPageContent(page)}
        </div>

        <div className="mt-3 flex items-center gap-4">
          <button
            onClick={prev}
            disabled={currentSinglePage === 0}
            className="p-2 rounded-full bg-editor-sidebar/80 text-editor-sidebar-foreground hover:bg-editor-sidebar disabled:opacity-20 transition-all"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="flex flex-col items-center">
            <span className="text-xs text-muted-foreground font-medium">{label}</span>
            <span className="text-[10px] text-muted-foreground">{currentSinglePage + 1} / {singlePages.length}</span>
          </div>
          <button
            onClick={next}
            disabled={currentSinglePage === singlePages.length - 1}
            className="p-2 rounded-full bg-editor-sidebar/80 text-editor-sidebar-foreground hover:bg-editor-sidebar disabled:opacity-20 transition-all"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    );
  }

  // ─── Spread mode (desktop) ───
  const displayIdx = flip ? (flip.direction === 'next' ? currentSpread + 1 : currentSpread - 1) : currentSpread;
  const displaySpread = spreads[displayIdx] ?? spreads[currentSpread];
  if (!displaySpread) return null;

  const isSingle = displaySpread.length === 1;

  const flipSpread = flip ? spreads[flip.fromSpread] : null;

  const shownSpread = spreads[currentSpread] ?? displaySpread;
  const isSingleShown = shownSpread.length === 1;
  let spreadLabel: string;
  if (isSingleShown) {
    spreadLabel = pageLabel(shownSpread[0]) || '';
  } else {
    const leftNum = pageNum(shownSpread[0]);
    const rightNum = pageNum(shownSpread[1]);
    const leftLabel = pageLabel(shownSpread[0]);
    const rightLabel = pageLabel(shownSpread[1]);
    if (leftNum !== null && rightNum !== null) {
      spreadLabel = `Pages ${leftNum}-${rightNum}`;
    } else if (leftLabel && rightLabel) {
      spreadLabel = `${leftLabel} – ${rightLabel}`;
    } else {
      spreadLabel = leftLabel || rightLabel || '';
    }
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 w-full flex flex-col items-center justify-center bg-editor-canvas relative select-none overflow-hidden"
    >
      <button
        onClick={prev}
        disabled={currentSpread === 0 || !!flip}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-editor-sidebar/80 text-editor-sidebar-foreground hover:bg-editor-sidebar disabled:opacity-20 transition-all"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={next}
        disabled={currentSpread === spreads.length - 1 || !!flip}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-editor-sidebar/80 text-editor-sidebar-foreground hover:bg-editor-sidebar disabled:opacity-20 transition-all"
      >
        <ChevronRight size={24} />
      </button>

      <div
        className="relative book-perspective"
        style={{
          width: isSingle ? pageW : pageW * 2 + SPREAD_GAP * scale,
          height: pageH,
        }}
      >
        <div className="flex" style={{ gap: SPREAD_GAP * scale }}>
          {displaySpread.map((page, i) => (
            <div
              key={page.id + '-' + i}
              className="relative shrink-0 editor-shadow overflow-hidden"
              style={{ width: pageW, height: pageH }}
              onClick={i === 0 && currentSpread > 0 ? prev : i === 1 && currentSpread < spreads.length - 1 ? next : undefined}
            >
              {renderPageContent(page)}
            </div>
          ))}
        </div>

        {flip && flipSpread && flip.direction === 'next' && flipSpread.length > 1 && (
          <div
            className="page-flip-right"
            style={{
              width: pageW,
              height: pageH,
              left: isSingle ? 0 : pageW + SPREAD_GAP * scale,
              top: 0,
              position: 'absolute',
            }}
          >
            <div className="page-front overflow-hidden" style={{ width: pageW, height: pageH }}>
              {renderPageContent(flipSpread[1] ?? flipSpread[0])}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(to right, rgba(255,255,255,0) 60%, rgba(255,255,255,0.15) 85%, rgba(255,255,255,0.3) 100%)',
                }}
              />
            </div>
            <div className="page-back overflow-hidden bg-white" style={{ width: pageW, height: pageH }}>
              {displaySpread.length > 0 && renderPageContent(displaySpread[0])}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(to left, rgba(0,0,0,0) 80%, rgba(0,0,0,0.1) 100%)',
                }}
              />
            </div>
          </div>
        )}

        {flip && flipSpread && flip.direction === 'prev' && flipSpread.length > 1 && (
          <div
            className="page-flip-left"
            style={{
              width: pageW,
              height: pageH,
              right: isSingle ? 0 : pageW + SPREAD_GAP * scale,
              top: 0,
              position: 'absolute',
            }}
          >
            <div className="page-front overflow-hidden" style={{ width: pageW, height: pageH }}>
              {renderPageContent(flipSpread[0])}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(to left, rgba(255,255,255,0) 60%, rgba(255,255,255,0.15) 85%, rgba(255,255,255,0.3) 100%)',
                }}
              />
            </div>
            <div className="page-back overflow-hidden bg-white" style={{ width: pageW, height: pageH }}>
              {displaySpread.length > 1 && renderPageContent(displaySpread[1])}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(to right, rgba(0,0,0,0) 80%, rgba(0,0,0,0.1) 100%)',
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <span className="text-xs text-muted-foreground font-medium">{spreadLabel}</span>
        <span className="text-xs text-muted-foreground">/ {totalPages}</span>
      </div>
    </div>
  );
};

export default PreviewView;
